import { FC } from "react";
import {
  Button,
  Divider,
  IconButton,
  Typography,
  Switch,
  Box,
  Tooltip,
} from "@mui/material";
import { observer } from "mobx-react";

import { RiInformation2Line } from "react-icons/ri";
import RefreshIcon from "../icons/RefreshIcon";
import { CloseIcon } from "../icons";

import Row from "../ui/core/Row";
import ColumnVisibilityButton from "./components/filters-components/ColumnVisibilityButton";
import { useClaimsStore } from "../../hooks/useClaimsStore";
import CoreKeywordSearch from "../ui/core/CoreKeywordSearch";
import { SaveViewActionModal } from "./components/filters-components/SaveViewActionModal";
import ClaimsFilterViewsDropdown from "./components/filters-components/ClaimsFilterViewsDropdown";
import ClaimsExportCsvButton from "./components/filters-components/ClaimsExportCsvButton";

const ClaimsFilters: FC = observer(() => {
  const {
    loadClaimsData,
    showMissingClaims,
    setShowMissingClaims,
    claimKeywordSearch,
    setClaimsPageKeywordSearch,
    clearFilters,
    hasFiltersApplied,
    totalClaimsCount,
  } = useClaimsStore();

  return (
    <Row
      sx={{
        justifyContent: "space-between",
        padding: { xs: "10px", sm: "10px", md: "10px", lg: "10px", xl: "12px" },
        height: "auto",
        gap: 1,
        flexWrap: "nowrap",
        alignItems: "center",
        overflowX: "auto",
        overflowY: "hidden",
        width: "100%",
      }}
    >
      <IconButton
        aria-label="Refresh"
        onClick={() => loadClaimsData()}
        sx={(theme) => ({
          border: "1px solid",
          borderRadius: "8px",
          width: 36,
          height: 36,
          flexShrink: 0,
          color: theme.palette.text.secondary,
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        })}
      >
        <RefreshIcon />
      </IconButton>

      <Box sx={{ minWidth: "200px", maxWidth: "350px", width: "100%" }}>
        <CoreKeywordSearch
          showSearchIcon
          placeholder="Search claims..."
          value={claimKeywordSearch}
          onChange={(value) => setClaimsPageKeywordSearch(value)}
        />
      </Box>

      <Tooltip arrow title={`Total claims: ${totalClaimsCount}`}>
        <RiInformation2Line size={17} style={{ flexShrink: 0 }} />
      </Tooltip>

      <Box sx={{ flex: 1 }} />

      <Box sx={{ display: "flex", alignItems: "center", mr: 1, gap: 0.5 }}>
        <Typography
          color={!showMissingClaims ? "text.primary" : "text.secondary"}
          sx={{ whiteSpace: "nowrap", fontSize: "0.875rem" }}
        >
          837 Claims
        </Typography>
        <Switch
          checked={showMissingClaims}
          onChange={(e) => {
            setShowMissingClaims(e.target.checked);
            // Reload data when toggle changes
            loadClaimsData();
          }}
          size="small"
        />
        <Typography
          color={showMissingClaims ? "text.primary" : "text.secondary"}
          sx={{ whiteSpace: "nowrap", fontSize: "0.875rem" }}
        >
          Unmatched EDIs
        </Typography>
      </Box>

      {hasFiltersApplied ? (
        <Button
          variant="text"
          startIcon={<CloseIcon />}
          onClick={() => clearFilters()}
          sx={{ minWidth: "120px", color: "error.dark" }}
        >
          Clear Filters
        </Button>
      ) : null}

      {hasFiltersApplied ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px",
            overflow: "hidden",
            flexShrink: 0,
            background: "white",
          }}
        >
          <SaveViewActionModal />
          <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
          <ClaimsFilterViewsDropdown bordered={false} />
        </Box>
      ) : (
        <ClaimsFilterViewsDropdown />
      )}

      <ColumnVisibilityButton />

      <ClaimsExportCsvButton />
    </Row>
  );
});

export default ClaimsFilters;
